import Logo from "@/assets/images/logo/logo-yeda-phone.svg";
import {Input} from "@/components/ui/input.tsx";
import {LoadingButton} from "@/components/ui/button.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Wrapper} from "./components/wrapper.tsx";
import {Link, useNavigate, useLocation} from "react-router";
import {ChangeEvent, FormEvent, useState} from "react";
import {Eye, EyeOff} from "lucide-react";
import {Head} from "@/layout/Head.tsx";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert.tsx";
import {useResetPasswordMutation} from "@/store/auth.ts";

export default function ResetPasswordPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const token = new URLSearchParams(location.search).get('token') ?? '';

    const [{password, password_confirm}, setData] = useState({password: '', password_confirm: ''});
    const [fieldError, setFieldError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [resetPassword, {isLoading}] = useResetPasswordMutation();

    const tokenMissing = !token;

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        const {name, value} = e.target;
        setData(prev => ({...prev, [name]: value}));
        setFieldError('');
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!password || !password_confirm) {
            return setFieldError('אנא מלא את כל השדות');
        }
        if (password !== password_confirm) {
            return setFieldError('הסיסמאות אינן תואמות');
        }
        if (password.length < 8) {
            return setFieldError('הסיסמה חייבת להכיל לפחות 8 תווים');
        }
        try {
            await resetPassword({token, password, password_confirm}).unwrap();
            navigate('/login?password_reset=true', {replace: true});
        } catch (err: any) {
            const errors = err?.data?.data;
            if (errors?.token) {
                setFieldError(errors.token);
            } else if (errors?.password) {
                setFieldError(errors.password);
            } else {
                setFieldError('אירעה שגיאה. אנא נסה שוב.');
            }
        }
    }

    return (
        <Wrapper>
            <Head title="איפוס סיסמה"/>

            {/* ── Form panel ── */}
            <div className="flex flex-col justify-center items-center w-full lg:w-1/2 overflow-y-auto px-6 py-10 sm:px-12 lg:px-14">
                <div className="w-full max-w-sm flex flex-col gap-7">

                    <div>
                        <img alt="ידע פון" src={Logo} className="h-10 w-auto"/>
                    </div>

                    <div>
                        <div className="text-2xl font-semibold text-gray-900">איפוס סיסמה</div>
                        <p className="text-sm text-gray-500 mt-1">הכנס סיסמה חדשה לחשבונך</p>
                    </div>

                    {tokenMissing ? (
                        <Alert variant="danger">
                            <AlertTitle>קישור לא תקין</AlertTitle>
                            <AlertDescription>
                                הקישור שהגעת ממנו אינו תקין.{' '}
                                <Link className="border-b border-dotted hover:opacity-80 transition" to="/login">
                                    חזור לדף הכניסה
                                </Link>
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                            <div>
                                <Label htmlFor="password">סיסמה חדשה</Label>
                                <div className="relative mt-2">
                                    <Input
                                        name="password"
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={handleChange}
                                        placeholder="לפחות 8 תווים"
                                        className="w-full h-10 px-3 pe-10 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        className="absolute inset-y-0 end-0 flex items-center px-3 text-gray-400 hover:text-gray-600 transition"
                                        tabIndex={-1}
                                        aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                                    >
                                        {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="password_confirm">אימות סיסמה</Label>
                                <div className="relative mt-2">
                                    <Input
                                        name="password_confirm"
                                        id="password_confirm"
                                        type={showConfirm ? 'text' : 'password'}
                                        value={password_confirm}
                                        onChange={handleChange}
                                        placeholder="הכנס שוב את הסיסמה"
                                        className="w-full h-10 px-3 pe-10 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(v => !v)}
                                        className="absolute inset-y-0 end-0 flex items-center px-3 text-gray-400 hover:text-gray-600 transition"
                                        tabIndex={-1}
                                        aria-label={showConfirm ? 'הסתר סיסמה' : 'הצג סיסמה'}
                                    >
                                        {showConfirm ? <EyeOff size={16}/> : <Eye size={16}/>}
                                    </button>
                                </div>
                            </div>
                            {fieldError && (
                                <Alert variant="danger">
                                    <AlertDescription className="text-sm">{fieldError}</AlertDescription>
                                </Alert>
                            )}
                            <LoadingButton isLoading={isLoading} size="lg" className="w-full mt-1">
                                שמור סיסמה חדשה
                            </LoadingButton>
                        </form>
                    )}

                    <div className="flex gap-1.5 items-center">
                        <Link className="text-sm text-gray-400 hover:text-gray-600 border-b border-dotted transition" to="/login">
                            חזור לדף הכניסה
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── Decorative panel – desktop only ── */}
            <ResetDecorativePanel/>
        </Wrapper>
    );
}

/* ─────────────────────────────────────────────
   Decorative right-side panel (reuses login style)
───────────────────────────────────────────── */
function ResetDecorativePanel() {
    return (
        <div
            className="hidden lg:block lg:w-1/2 shrink-0 relative overflow-hidden"
            style={{
                background: [
                    'radial-gradient(ellipse at 44% 22%, rgba(130, 180, 255, 0.38) 0%, transparent 54%)',
                    'radial-gradient(ellipse at 72% 76%, rgba(200, 90, 30, 0.48) 0%, transparent 48%)',
                    'linear-gradient(158deg, #19293f 0%, #1b3656 22%, #1d3e63 50%, #5c2810 78%, #351208 100%)',
                ].join(', '),
            }}
        >
            <div className="absolute inset-0 flex flex-col justify-center px-12 gap-8">
                <div className="text-white">
                    <p className="text-3xl font-bold leading-snug">
                        אבטחת <br/> החשבון שלך
                    </p>
                    <p className="text-sky-200 mt-3 text-sm leading-relaxed max-w-xs">
                        הסיסמה החדשה שלך תהיה בתוקף לאחר אישור השינוי.
                        מומלץ להשתמש בסיסמה חזקה הכוללת אותיות, מספרים וסמלים.
                    </p>
                </div>
            </div>
        </div>
    );
}
